import { useFormContext, useWatch } from "react-hook-form";
import { Globe } from "lucide-react";
import {
  COACHING_TYPE,
  LIGHTING,
  RECEPTION_CAPACITY_TYPE,
  SURFACE_AREA,
  VENUE_TYPE,
} from "@academy-context/shared/domain/types/studio-environment";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared-kernel/primary/shared/shadcn/ui/select";
import { Checkbox } from "@shared-kernel/primary/shared/shadcn/ui/checkbox";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { UrlInput } from "@shared-kernel/primary/shared/url-input/url-input";
import { Label } from "@shared-kernel/primary/shared/shadcn/ui/label";

const VENUE_TYPE_OPTIONS: Array<{ value: VENUE_TYPE; label: string }> = [
  { value: VENUE_TYPE.PERSONAL_STUDIO, label: "Studio personnel" },
  { value: VENUE_TYPE.RENTAL_STUDIO, label: "Studio en location" },
];

const COACHING_TYPE_OPTIONS: Array<{ value: COACHING_TYPE; label: string }> = [
  { value: COACHING_TYPE.VOICE, label: "Chant / voix" },
  { value: COACHING_TYPE.DANCE, label: "Danse" },
  { value: COACHING_TYPE.INSTRUMENT, label: "Instrument" },
  { value: COACHING_TYPE.ARTIST_WELLBEING, label: "Bien-être et pratique corporelle de l'artiste" },
];

const SURFACE_AREA_OPTIONS: Array<{ value: SURFACE_AREA; label: string }> = [
  { value: SURFACE_AREA.UNDER_10, label: "Moins de 10 m²" },
  { value: SURFACE_AREA.FROM_10_TO_20, label: "De 10 à 20 m²" },
  { value: SURFACE_AREA.OVER_20, label: "Plus de 20 m²" },
];

const LIGHTING_OPTIONS: Array<{ value: LIGHTING; label: string }> = [
  { value: LIGHTING.NATURAL, label: "Naturel" },
  { value: LIGHTING.ARTIFICIAL, label: "Artificiel" },
];

const RECEPTION_CAPACITY_OPTIONS: Array<{ value: RECEPTION_CAPACITY_TYPE; label: string }> = [
  { value: RECEPTION_CAPACITY_TYPE.INDIVIDUAL, label: "Individuel" },
  { value: RECEPTION_CAPACITY_TYPE.MASTERCLASS, label: "Masterclass" },
];

const MATERIAL_BOOLEAN_FIELDS: Array<{ suffix: string; label: string }> = [
  { suffix: "materialConditions.hasVentilation", label: "Ventilation / aération" },
  { suffix: "materialConditions.hasHeating", label: "Chauffage" },
  { suffix: "materialConditions.hasWaterAndSanitary", label: "Point d'eau et sanitaires à proximité" },
  { suffix: "materialConditions.hasSoundproofing", label: "Insonorisation" },
];

const SAFETY_BOOLEAN_FIELDS: Array<{ suffix: string; label: string }> = [
  { suffix: "safetyConditions.hasSmokeDetector", label: "Détecteur de fumée" },
  { suffix: "safetyConditions.hasFireExtinguisher", label: "Extincteur" },
];

interface Props {
  // Path of the environment object inside the form, e.g. "environment" or
  // "studio.details.environment".
  namePrefix: string;
}

export const StudioEnvironmentFields = ({ namePrefix }: Props) => {
  const { control, setValue } = useFormContext();

  const receptionType = useWatch({ control, name: `${namePrefix}.receptionCapacity.type` });

  return (
    <div className="space-y-6 border-t pt-6">
      <h3 className="text-base font-semibold">Environnement mis à disposition</h3>

      <div className="space-y-4">
        <FormField
          control={control}
          name={`${namePrefix}.venueType`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type de lieu</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un type de lieu" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {VENUE_TYPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${namePrefix}.externalUrl`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Lien externe du studio (site web, réservation en ligne)</FormLabel>
              <FormControl>
                <UrlInput {...field} icon={<Globe />} label="Lien" placeholder="mon-studio.fr" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name={`${namePrefix}.coachingTypes`}
        render={({ field }) => {
          const selected: COACHING_TYPE[] = field.value ?? [];
          const toggle = (value: COACHING_TYPE, checked: boolean) => {
            field.onChange(checked ? [...selected, value] : selected.filter(item => item !== value));
          };
          return (
            <FormItem>
              <FormLabel>Typologies de coaching accueillies</FormLabel>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {COACHING_TYPE_OPTIONS.map(option => (
                  <label key={option.value} className="flex cursor-pointer items-center space-x-2">
                    <Checkbox
                      checked={selected.includes(option.value)}
                      onCheckedChange={checked => toggle(option.value, checked === true)}
                    />
                    <span className="text-sm font-normal">{option.label}</span>
                  </label>
                ))}
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          control={control}
          name={`${namePrefix}.receptionCapacity.type`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Capacité d&apos;accueil</FormLabel>
              <Select
                value={field.value}
                onValueChange={value => {
                  field.onChange(value);
                  if (value === RECEPTION_CAPACITY_TYPE.INDIVIDUAL) {
                    setValue(`${namePrefix}.receptionCapacity.maxPeople`, null, { shouldValidate: true });
                  }
                }}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez une capacité" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {RECEPTION_CAPACITY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {receptionType === RECEPTION_CAPACITY_TYPE.MASTERCLASS && (
          <FormField
            control={control}
            name={`${namePrefix}.receptionCapacity.maxPeople`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre maximum de personnes</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={field.value ?? ""}
                    onChange={event => field.onChange(event.target.value === "" ? null : event.target.valueAsNumber)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      <div className="space-y-4 border-t pt-6">
        <Label>Conditions matérielles</Label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name={`${namePrefix}.materialConditions.surfaceArea`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Surface</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez une surface" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {SURFACE_AREA_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={`${namePrefix}.materialConditions.lighting`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Éclairage</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un éclairage" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {LIGHTING_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {MATERIAL_BOOLEAN_FIELDS.map(({ suffix, label }) => (
            <FormField
              key={suffix}
              control={control}
              name={`${namePrefix}.${suffix}`}
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="cursor-pointer font-normal">{label}</FormLabel>
                </FormItem>
              )}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3 border-t pt-6">
        <Label>Conditions de sécurité</Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {SAFETY_BOOLEAN_FIELDS.map(({ suffix, label }) => (
            <FormField
              key={suffix}
              control={control}
              name={`${namePrefix}.${suffix}`}
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="cursor-pointer font-normal">{label}</FormLabel>
                </FormItem>
              )}
            />
          ))}
        </div>
      </div>

      <FormField
        control={control}
        name={`${namePrefix}.hasHostingHonorAttestation`}
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer">
                J&apos;atteste sur l&apos;honneur que le studio peut accueillir l&apos;enseignement dans des conditions de sécurité et
                d&apos;hygiène correctes.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};
